import { RefObject, useEffect, useRef } from 'react';
import { useIntersection } from 'react-use';
import { PostCardProps } from '../../PostCard/PostCard';
import { PostUI } from '../../types';

interface UseTrackPostVisibleProps {
    onVisibilityChange?: PostCardProps['onVisibilityChange'];
    post: PostUI;
    index: number;
    activeIndex: number;
    postRef: RefObject<HTMLDivElement>;
}

export const useTrackPostVisible = ({
    postRef,
    onVisibilityChange,
    post,
    index,
    activeIndex,
}: UseTrackPostVisibleProps) => {
    const isVisibleRef = useRef(false);

    const intersection = useIntersection(postRef, {
        root: null,
        threshold: 0.5,
    });

    useEffect(() => {
        if (!intersection) {
            return;
        }

        const isVisible = intersection.intersectionRatio >= 0.5;
        if (isVisible !== isVisibleRef.current) {
            isVisibleRef.current = isVisible;

            onVisibilityChange?.({
                mediaId: post.content[activeIndex].id,
                isInViewport: isVisible,
                postNumber: index,
            });
        }
    }, [
        activeIndex,
        intersection?.intersectionRatio,
        onVisibilityChange,
        intersection,
        post.content,
        index,
        postRef,
    ]);

    // Создаем обновляемую функцию отдельно от деструктора, чтобы лишний раз не тригерить пуш ивента при изменении параметров
    const destructorCbRef = useRef<VoidFunction>();
    useEffect(() => {
        destructorCbRef.current = () =>
            onVisibilityChange?.({
                mediaId: post.content[activeIndex].id,
                isInViewport: false,
                postNumber: index,
            });
    }, [activeIndex, post, onVisibilityChange, index]);

    // деструктор вызывает ивент окончания просмотра поста если он во вьюпорте
    useEffect(
        () => () => {
            isVisibleRef.current && destructorCbRef.current?.();
        },
        [],
    );

    return { postRef };
};
